/**
 * v6.2 · Email Reports (Reporting → Email Reports)
 *
 * VC-admin CRUD for weekly / monthly scheduled report emails.
 * Backend: /api/v62/email-reports (+ /toggle, /send-now).
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import ReportsSubNav from "@/components/ReportsSubNav";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Send, Mail, Calendar, Users as UsersIcon,
  X, ChevronDown, Power, PowerOff, AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";

const WEEKDAYS = [
  { v: "monday", label: "Monday" }, { v: "tuesday", label: "Tuesday" },
  { v: "wednesday", label: "Wednesday" }, { v: "thursday", label: "Thursday" },
  { v: "friday", label: "Friday" }, { v: "saturday", label: "Saturday" },
  { v: "sunday", label: "Sunday" },
];
const MONTH_DAYS = [
  ...Array.from({ length: 28 }, (_, i) => ({ v: String(i + 1), label: `Day ${i + 1}` })),
  { v: "last", label: "Last day of month" },
];
const TIMES = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (const mi of [0, 30]) {
      out.push(`${String(h).padStart(2, "0")}:${String(mi).padStart(2, "0")}`);
    }
  }
  return out;
})();

function fmtIST(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const opts = { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" };
    return d.toLocaleString("en-GB", opts).replace(",", "") + " IST";
  } catch { return iso; }
}

function scheduleText(r) {
  if (r.report_type === "weekly") {
    const wd = WEEKDAYS.find((w) => w.v === r.send_day)?.label || r.send_day;
    return `Every ${wd} at ${r.send_time} IST`;
  }
  const dayLabel = r.send_day === "last" ? "the last day" : `day ${r.send_day}`;
  return `Every month on ${dayLabel} at ${r.send_time} IST`;
}

export default function EmailReports() {
  const { user } = useAuth();
  const canManage = user && (user.role === "vc_admin" || user.role === "senior_vc");
  const [rows, setRows] = useState([]);
  const [reports, setReports] = useState([]);
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | row
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [rres, sres, mres] = await Promise.all([
        api.get("/v62/email-reports"),
        api.get("/v62/shared/reports"),
        api.get("/team-members"),
      ]);
      setRows(rres.data || []);
      setReports(sres.data || []);
      setMembers(mres.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    try {
      await api.post(`/v62/email-reports/${r.report_id}/toggle`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.report_name}"?`)) return;
    try {
      await api.delete(`/v62/email-reports/${r.report_id}`);
      toast.success("Email report deleted");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const sendNow = async (r) => {
    try {
      await api.post(`/v62/email-reports/${r.report_id}/send-now`);
      toast.success("Send triggered — check recipients' inbox.");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8">
      <div className="cw-caption uppercase tracking-widest">Reporting</div>
      <div className="mt-1 flex items-center justify-between gap-4">
        <h1 className="cw-h1" data-testid="email-reports-title">Email Reports</h1>
        {canManage && (
          <button
            onClick={() => setEditing("new")}
            className="cw-btn-primary inline-flex items-center gap-2"
            data-testid="add-email-report-btn"
          >
            <Plus size={14} /> Add Report
          </button>
        )}
      </div>
      <p className="text-sm text-[var(--cw-muted)] mt-1">
        Send a report by email on a weekly or monthly schedule, to people in your firm or outside it.
      </p>
      <div className="mt-4"><ReportsSubNav /></div>

      {loading ? (
        <div className="mt-8 cw-card p-10 text-center text-[var(--cw-muted)]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="mt-8 cw-card p-10 text-center" data-testid="email-reports-empty">
          <Mail size={28} className="mx-auto opacity-40" />
          <div className="mt-3 font-medium">No email reports yet</div>
          <div className="text-sm text-[var(--cw-muted)] mt-1">
            Schedule a report to be delivered by email to your team or investors.
          </div>
          {canManage && (
            <button onClick={() => setEditing("new")} className="cw-btn-primary mt-4 inline-flex items-center gap-2"
                    data-testid="add-first-email-report-btn">
              <Plus size={14} /> Add Report
            </button>
          )}
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="email-reports-list">
          {rows.map((r) => (
            <ReportRow
              key={r.report_id}
              row={r}
              reports={reports}
              members={members}
              canManage={canManage}
              onEdit={() => setEditing(r)}
              onDelete={() => del(r)}
              onToggle={() => toggle(r)}
              onSendNow={() => sendNow(r)}
            />
          ))}
        </div>
      )}

      {editing && (
        <ReportFormModal
          initial={editing === "new" ? null : editing}
          reports={reports}
          members={members}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ReportRow({ row, reports, members, canManage, onEdit, onDelete, onToggle, onSendNow }) {
  const reportLabel = reports.find((r) => r.report_id === row.report_id_ref)?.label || row.report_id_ref;
  const recipientUsers = (row.recipient_user_ids || [])
    .map((uid) => members.find((m) => m.user_id === uid))
    .filter(Boolean);
  const otherEmails = row.other_emails || [];
  const totalRecipients = recipientUsers.length + otherEmails.length;
  const firstThree = [
    ...recipientUsers.slice(0, 3).map((u) => u.full_name || u.email),
    ...otherEmails.slice(0, Math.max(0, 3 - recipientUsers.length)),
  ];
  const remaining = totalRecipients - firstThree.length;

  const isPausedByTenant = row.tenant_inactive === true;
  const isPausedByUser = !row.is_active && !isPausedByTenant;
  const badExists = row.last_status && row.last_status !== "sent" && row.last_status !== null;

  return (
    <div
      className="cw-card p-4"
      style={{ opacity: row.is_active ? 1 : 0.7 }}
      data-testid={`email-report-row-${row.report_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold">{row.report_name || reportLabel}</div>
            <span className="cw-badge uppercase text-[10px]"
                  style={{ background: "rgba(69,87,204,0.10)", color: "var(--cw-primary)" }}>
              {row.report_type}
            </span>
            {isPausedByUser && (
              <span className="cw-badge" style={{ background: "rgba(0,0,0,0.06)", color: "#666" }}>Paused</span>
            )}
            {isPausedByTenant && (
              <span className="cw-badge inline-flex items-center gap-1"
                    style={{ background: "rgba(224,161,0,0.15)", color: "#7A5900" }}>
                <AlertTriangle size={10} /> Paused (tenant inactive)
              </span>
            )}
          </div>
          <div className="text-xs text-[var(--cw-muted)] mt-1 inline-flex items-center gap-1">
            <Calendar size={10} /> {scheduleText(row)}
          </div>
          <div className="text-xs mt-1 inline-flex items-start gap-1">
            <UsersIcon size={12} className="mt-0.5 shrink-0 text-[var(--cw-muted)]" />
            <span>
              {firstThree.length ? firstThree.join(", ") : "No recipients"}
              {remaining > 0 && ` +${remaining} more`}
            </span>
          </div>
          <div className="text-xs text-[var(--cw-muted)] mt-1">
            Next send: {row.is_active && !isPausedByTenant ? fmtIST(row.next_run_at) : "—"}
            {row.last_status && (
              <span className="ml-3">
                Last: <span style={badExists ? { color: "var(--cw-error)" } : {}}>{row.last_status}</span>
                {row.last_run_at && ` · ${fmtIST(row.last_run_at)}`}
              </span>
            )}
          </div>
        </div>
        {canManage && (
          <div className="flex gap-1 shrink-0 items-center">
            <button onClick={onToggle}
              title={row.is_active ? "Pause" : "Resume"}
              className="cw-btn-ghost text-[10px] inline-flex items-center gap-1"
              data-testid={`email-report-toggle-${row.report_id}`}>
              {row.is_active ? <><PowerOff size={10} /> Pause</> : <><Power size={10} /> Resume</>}
            </button>
            <button onClick={onSendNow}
              title="Send test / now"
              className="cw-btn-ghost text-[10px] inline-flex items-center gap-1"
              data-testid={`email-report-sendnow-${row.report_id}`}>
              <Send size={10} /> Send now
            </button>
            <button onClick={onEdit}
              className="cw-btn-ghost text-[10px] inline-flex items-center gap-1"
              data-testid={`email-report-edit-${row.report_id}`}>
              <Pencil size={10} /> Edit
            </button>
            <button onClick={onDelete}
              className="cw-btn-ghost text-[10px] text-[var(--cw-error)]"
              data-testid={`email-report-delete-${row.report_id}`}>
              <Trash2 size={10} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

function ReportFormModal({ initial, reports, members, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    report_type: initial?.report_type || "weekly",
    report_id_ref: initial?.report_id_ref || (reports[0]?.report_id ?? ""),
    send_day: initial?.send_day || "monday",
    send_time: initial?.send_time || "09:00",
    recipient_user_ids: initial?.recipient_user_ids || [],
    other_emails: initial?.other_emails || [],
    report_name: initial?.report_name || "",
  }));
  const [includeOthers, setIncludeOthers] = useState(
    !!(initial?.other_emails && initial.other_emails.length)
  );
  const [emailDraft, setEmailDraft] = useState("");
  const [emailError, setEmailError] = useState("");
  const [saving, setSaving] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");

  // When switching report_type, reset send_day to a sensible default.
  useEffect(() => {
    setForm((f) => ({
      ...f,
      send_day: f.report_type === "weekly" ? "monday" : "1",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.report_type]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const activeMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    return (members || [])
      .filter((m) => m.is_active !== false)
      .filter((m) => !q || (m.full_name || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q));
  }, [members, memberSearch]);
  const toggleUser = (uid) => setForm((f) => {
    const has = (f.recipient_user_ids || []).includes(uid);
    return {
      ...f,
      recipient_user_ids: has
        ? f.recipient_user_ids.filter((u) => u !== uid)
        : [...(f.recipient_user_ids || []), uid],
    };
  });
  const selectAll = () => setForm((f) => ({ ...f, recipient_user_ids: activeMembers.map((m) => m.user_id) }));
  const clearAll = () => setForm((f) => ({ ...f, recipient_user_ids: [] }));

  const parseAndAddEmails = (raw) => {
    const parts = raw
      .split(/[\s,;\n]+/)
      .map((p) => p.trim().toLowerCase())
      .filter(Boolean);
    if (parts.length === 0) return;
    const memberEmails = new Set((members || []).map((m) => (m.email || "").toLowerCase()));
    const added = [];
    const badBucket = [];
    setForm((f) => {
      const nextOthers = [...(f.other_emails || [])];
      const nextUsers = [...(f.recipient_user_ids || [])];
      for (const p of parts) {
        if (!EMAIL_RE.test(p)) {
          badBucket.push(p);
          continue;
        }
        if (memberEmails.has(p)) {
          const u = members.find((m) => (m.email || "").toLowerCase() === p);
          if (u && !nextUsers.includes(u.user_id)) nextUsers.push(u.user_id);
          continue;
        }
        if (!nextOthers.includes(p)) {
          nextOthers.push(p);
          added.push(p);
        }
      }
      return { ...f, other_emails: nextOthers, recipient_user_ids: nextUsers };
    });
    if (badBucket.length) {
      setEmailError(`"${badBucket[0]}" is not a valid email address.`);
    } else {
      setEmailError("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.report_id_ref) { toast.error("Choose a report."); return; }
    const otherEmails = includeOthers ? (form.other_emails || []) : [];
    const total = (form.recipient_user_ids || []).length + otherEmails.length;
    if (total < 1) { toast.error("Add at least one recipient."); return; }
    if (total > 50) { toast.error("Maximum 50 recipients per email report."); return; }
    setSaving(true);
    try {
      const payload = {
        report_type: form.report_type,
        report_id_ref: form.report_id_ref,
        send_day: form.send_day,
        send_time: form.send_time,
        recipient_user_ids: form.recipient_user_ids || [],
        other_emails: otherEmails,
        report_name: form.report_name || null,
      };
      if (initial?.report_id) {
        await api.patch(`/v62/email-reports/${initial.report_id}`, payload);
        toast.success("Email report updated.");
      } else {
        await api.post("/v62/email-reports", payload);
        toast.success("Email report scheduled.");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save email report.");
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full h-9 px-3 rounded-md border text-sm bg-white";
  const border = { borderColor: "var(--cw-border)" };
  const reportLabel = reports.find((r) => r.report_id === form.report_id_ref)?.label || "";
  const summaryPeriod = form.report_type === "weekly"
    ? "the previous Monday to Sunday"
    : "the previous calendar month";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         data-testid="email-report-modal">
      <form onSubmit={submit}
            className="bg-white rounded-xl w-full max-w-xl max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={border}>
          <h2 className="cw-h2">{initial?.report_id ? "Edit Email Report" : "Schedule Email Report"}</h2>
          <button type="button" onClick={onClose} className="text-[var(--cw-muted)]"
                  data-testid="email-report-close-btn"><X size={18} /></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          <div>
            <div className="cw-caption">Report type *</div>
            <div className="mt-1 flex gap-2">
              {["weekly", "monthly"].map((t) => (
                <label key={t}
                       className={`px-3 h-9 inline-flex items-center gap-2 rounded-md border text-sm cursor-pointer ${
                         form.report_type === t ? "font-medium" : ""
                       }`}
                       style={{ borderColor: form.report_type === t ? "var(--cw-primary)" : "var(--cw-border)",
                                background: form.report_type === t ? "rgba(69,87,204,0.06)" : "#fff" }}>
                  <input type="radio" className="hidden"
                         checked={form.report_type === t}
                         onChange={() => setF("report_type", t)}
                         data-testid={`email-report-type-${t}`} />
                  {t === "weekly" ? "Weekly" : "Monthly"}
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="cw-caption">Which report *</div>
            <div className="relative mt-1">
              <select value={form.report_id_ref}
                      onChange={(e) => setF("report_id_ref", e.target.value)}
                      className={inp + " appearance-none pr-8"} style={border}
                      data-testid="email-report-ref-select">
                {reports.map((r) => (
                  <option key={r.report_id} value={r.report_id}>{r.label}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="cw-caption">Send every *</div>
              <select value={form.send_day}
                      onChange={(e) => setF("send_day", e.target.value)}
                      className={inp + " mt-1"} style={border}
                      data-testid="email-report-day-select">
                {(form.report_type === "weekly" ? WEEKDAYS : MONTH_DAYS).map((d) => (
                  <option key={d.v} value={d.v}>{d.label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="cw-caption">Time (IST) *</div>
              <select value={form.send_time}
                      onChange={(e) => setF("send_time", e.target.value)}
                      className={inp + " mt-1"} style={border}
                      data-testid="email-report-time-select">
                {TIMES.map((t) => (<option key={t} value={t}>{t}</option>))}
              </select>
            </div>
          </div>

          <div>
            <div className="cw-caption">Report name (optional)</div>
            <input value={form.report_name}
                   onChange={(e) => setF("report_name", e.target.value)}
                   placeholder={reportLabel}
                   className={inp + " mt-1"} style={border}
                   data-testid="email-report-name-input" />
          </div>

          <div className="text-xs text-[var(--cw-muted)] p-3 rounded-md border" style={border}>
            <strong>{reportLabel}</strong> will be sent {form.report_type === "weekly"
              ? `every ${WEEKDAYS.find((w) => w.v === form.send_day)?.label || form.send_day} at ${form.send_time} IST`
              : `every month on ${form.send_day === "last" ? "the last day" : `day ${form.send_day}`} at ${form.send_time} IST`
            }, covering {summaryPeriod}. Recipients get a snapshot of the report and a link to view it in full.
          </div>

          <div>
            <div className="flex items-center justify-between">
              <div className="cw-caption">Recipients *</div>
              <div className="flex gap-2 text-[11px]">
                <button type="button" className="cw-btn-ghost text-[11px]" onClick={selectAll}
                        data-testid="email-report-select-all">Select all</button>
                <button type="button" className="cw-btn-ghost text-[11px]" onClick={clearAll}
                        data-testid="email-report-clear-all">Clear</button>
              </div>
            </div>
            <input value={memberSearch}
                   onChange={(e) => setMemberSearch(e.target.value)}
                   placeholder="Search team members…"
                   className={inp + " mt-1"} style={border} />
            <div className="mt-2 max-h-52 overflow-y-auto border rounded-md" style={border}
                 data-testid="email-report-members-list">
              {activeMembers.length === 0 && (
                <div className="p-3 text-xs text-[var(--cw-muted)]">No active users match.</div>
              )}
              {activeMembers.map((m) => {
                const checked = (form.recipient_user_ids || []).includes(m.user_id);
                return (
                  <label key={m.user_id}
                         className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-black/[0.02] cursor-pointer border-b last:border-b-0"
                         style={border}
                         data-testid={`email-report-member-${m.user_id}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleUser(m.user_id)} />
                    <span className="flex-1 truncate">
                      <span className="font-medium">{m.full_name || m.email}</span>
                      <span className="text-[var(--cw-muted)] ml-2">{m.email}</span>
                    </span>
                    <span className="text-[11px] cw-badge" style={{ background: "rgba(0,0,0,0.05)", color: "#666" }}>
                      {m.role}
                    </span>
                  </label>
                );
              })}
              <label className="flex items-center gap-2 px-3 py-2 text-sm bg-black/[0.02] cursor-pointer"
                     data-testid="email-report-others-toggle">
                <input type="checkbox" checked={includeOthers}
                       onChange={(e) => setIncludeOthers(e.target.checked)} />
                <span className="flex-1">
                  <span className="font-medium">Others</span>
                  <span className="text-[var(--cw-muted)] ml-2">Send to email addresses outside your firm</span>
                </span>
              </label>
            </div>
            {includeOthers && (
              <div className="mt-2">
                <div className="cw-caption">Other email addresses</div>
                <div className="mt-1 flex flex-wrap gap-1 p-2 border rounded-md" style={border}>
                  {(form.other_emails || []).map((em) => (
                    <span key={em} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                          style={{ background: "rgba(69,87,204,0.10)", color: "var(--cw-primary)" }}
                          data-testid={`email-report-other-tag-${em}`}>
                      {em}
                      <button type="button" onClick={() => setF("other_emails", form.other_emails.filter((e) => e !== em))}>
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                  <input value={emailDraft}
                         onChange={(e) => { setEmailDraft(e.target.value); setEmailError(""); }}
                         onKeyDown={(e) => {
                           if (e.key === "Enter" || e.key === ",") {
                             e.preventDefault();
                             parseAndAddEmails(emailDraft);
                             setEmailDraft("");
                           }
                         }}
                         onBlur={() => { if (emailDraft.trim()) { parseAndAddEmails(emailDraft); setEmailDraft(""); } }}
                         onPaste={(e) => {
                           const text = e.clipboardData.getData("text");
                           if (/[\s,;\n]/.test(text)) {
                             e.preventDefault();
                             parseAndAddEmails(text);
                             setEmailDraft("");
                           }
                         }}
                         placeholder="type an email, press Enter…"
                         className="flex-1 min-w-[160px] text-sm px-1 py-0.5 outline-none"
                         data-testid="email-report-others-input" />
                </div>
                {emailError && (
                  <div className="mt-1 text-[11px]" style={{ color: "var(--cw-error)" }}
                       data-testid="email-report-other-email-error">
                    {emailError}
                  </div>
                )}
              </div>
            )}
            <div className="mt-2 text-[11px] text-[var(--cw-muted)]"
                 data-testid="email-report-recipients-count">
              {(form.recipient_user_ids || []).length + (includeOthers ? (form.other_emails || []).length : 0)} / 50 recipients selected
            </div>
          </div>
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-2" style={border}>
          <button type="button" onClick={onClose} className="cw-btn-ghost text-sm">Cancel</button>
          <button disabled={saving} type="submit" className="cw-btn-primary text-sm"
                  data-testid="email-report-submit-btn">
            {saving ? "Saving…" : (initial?.report_id ? "Update Report" : "Schedule Report")}
          </button>
        </div>
      </form>
    </div>
  );
}
